import { ArrowRight, Loader2, ShoppingBag } from 'lucide-react';
import { useShopCollections } from '../../hooks/useShopify';
import { shopifyConfigError, type ShopCollection } from '../../lib/shopify';
import { useShopStore } from '../../store/shopStore';

interface ShopScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

export default function ShopScreen({ onNavigate }: ShopScreenProps) {
  const { collections, error, isLoading, isConfigured } = useShopCollections();
  const cartCount = useShopStore((state) => state.lines.reduce((sum, line) => sum + line.quantity, 0));

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#D4F04A]/20 text-[#1A6B4F] flex items-center justify-center">
          <ShoppingBag className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg">Shop</h1>
          <p className="text-xs text-muted-foreground">Browse collections powered by Shopify</p>
        </div>
        <button
          onClick={() => onNavigate('shop-cart')}
          className="relative w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          aria-label="Open cart"
        >
          <ShoppingBag className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#1A6B4F] text-white text-[10px] flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </div>

      <div className="px-6 py-6 space-y-5">
        {!isConfigured && (
          <MessageCard
            title="Shop not configured"
            body={shopifyConfigError ?? 'Shopify env vars are missing.'}
          />
        )}

        {error && (
          <MessageCard title="Could not load collections" body={error.message} />
        )}

        {isConfigured && isLoading && (
          <div className="py-16 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading collections…
          </div>
        )}

        {isConfigured && !isLoading && !error && collections.length === 0 && (
          <MessageCard
            title="No collections available"
            body="Publish collections to the Shopify storefront and they will appear here."
          />
        )}

        {isConfigured && collections.length > 0 && (
          <section className="space-y-3">
            <div>
              <div>
                <h3 className="text-base">Collections</h3>
                <p className="text-xs text-muted-foreground">Browse the live Shopify collections available in Deuce.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {collections.map((collection) => (
                <CollectionHeroCard
                  key={collection.id}
                  collection={collection}
                  onOpen={() => onNavigate('shop-collection', { collectionHandle: collection.handle })}
                />
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
}

function CollectionHeroCard({
  collection,
  onOpen,
}: {
  collection: ShopCollection;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="w-full overflow-hidden rounded-[1.9rem] border border-border bg-card text-left transition-colors hover:border-[#1A6B4F]/40"
    >
      <div className="relative h-56 bg-secondary">
        {collection.image ? (
          <img
            src={collection.image.url}
            alt={collection.image.altText ?? collection.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[radial-gradient(circle_at_top_left,_rgba(198,248,6,0.24),_transparent_35%),linear-gradient(135deg,#1A6B4F_0%,#18260d_100%)]" />
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-[#14563F]">
            Collection
          </div>
          <div className="mt-3 text-2xl leading-tight text-white">{collection.title}</div>
        </div>
      </div>
      <div className="space-y-3 p-4">
        <div className="line-clamp-3 text-sm text-muted-foreground">
          {collection.description || 'Open this collection to browse curated Shopify products inside Deuce.'}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Open collection</div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}

function MessageCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="text-sm font-medium">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{body}</div>
    </div>
  );
}